/**
 * The cards of the reset by E and of the mailed links (part 36c, tables RQ, LK, CH4, FX3, DA3; built in the gate's cards,
 * the user's choice 2026-10-24): "Forgot password?" asking the link; the reset link's new password; a change's link
 * confirmed on the device that asked; the enrolment's link going on with the second step's setup and its codes; the
 * deletion's cancel link. Each link's token came out of the address at once (C12); a card needs no book and no lock,
 * so it shows over any screen (36a's VL). A deletion's cancel sends nothing before its button is pressed.
 */
import { createEffect, createSignal } from 'solid-js';
import { canonicalEmail } from '../account/email.ts';
import type { NewFactorProof } from '../account/actions.ts';
import type { FactorSetup } from '../account/wire.ts';
import { Back, EmailField, ErrorText, focusField, Heading, Submit } from './account-fields.ts';
import { actionProblem, advancedWords, CodesView, Illustration, SetupStep, SheetNewPassword, timeWords } from './account-sheet.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import Icon from '../ui/Icon.ts';
import { component, el } from '../ui/solid.ts';
import type { Child } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** Deep-fix r3, LK (36a's VL5 for every link a device must confirm itself): opened in the browser outside the iOS Home Screen app, the note to paste it there. */
function HomeScreenNote(shell: Shell): Child {
  return shell.homeScreen.state() === 'none' ? null : el('p', { class: 'ac-note' }, component(Icon, { name: 'info', size: 'sm' }), el('span', {}, shell.t('account.link.homeScreen')));
}

/** A link card's kind (LK). */
export type LinkStep = 'reset' | 'confirm' | 'enrol' | 'keep';

/** Puts the link away: the card closed, then its token forgotten (so the card never shows again without one). */
function closeLink(shell: Shell): void {
  shell.ui.setAccountStep(null);
  shell.ui.setLinkToken(null);
}

/** A card's closing button (Back to my books). */
function CloseButton(shell: Shell, label: string = shell.t('account.verify.keep')): Child {
  return component(Button, { size: 'lg', fullWidth: true, class: 'link-close', onClick: () => { closeLink(shell); }, children: label });
}

/** A card's "Not now" (the library's link Button in its line): the link put away, nothing sent. */
function Later(shell: Shell): Child {
  return el('p', { class: 'ac-muted ac-center' }, component(Button, { variant: 'link', onClick: () => { closeLink(shell); }, children: shell.t('account.verify.later') }));
}

/** A card that says one thing and closes (a link done, with the design's done mark when `ok`; one that can't be used). */
function SaidCard(shell: Shell, id: string, title: string, words: string, className: string, ok = false): Child {
  return el(
    'section',
    { class: `ac-card ac-form ${className}`, 'aria-labelledby': id },
    // The design's done step: its progress line, the heading, then the mark.
    ok ? el('p', { class: 'ac-sheet__progress' }, shell.t('account.sheet.security')) : null,
    Heading(id, title, 'ac-form__title'),
    ok ? Illustration('check', true) : null,
    el('p', { class: 'ac-lead', role: 'status' }, words),
    CloseButton(shell),
  );
}

/** RQ1: "Forgot password?": the address, then "check your email" (the same for every address, RS2). */
export function ForgotCard(shell: Shell): Child {
  const { t, manager, ui } = shell;
  const [email, setEmail] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [sent, setSent] = createSignal<string | null>(null);
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    const address = canonicalEmail(email());
    if (busy()) return;
    if (address === null) {
      setError(t('account.error.email'));
      focusField('fg-email');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await manager.requestReset(address);
    setBusy(false);
    if (outcome.kind === 'ok') setSent(address);
    else setError(actionProblem(shell, outcome));
  };
  return () => {
    const address = sent();
    if (address !== null) return SaidCard(shell, 'fg-title', t('account.check.title'), t('account.forgot.check', { email: address }), 'forgot-sent');
    return el(
      'form',
      { class: 'ac-card ac-form forgot-password-card', 'aria-labelledby': 'fg-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
      Back(shell, () => ui.setAccountStep('login')),
      Heading('fg-title', t('account.forgot.title'), 'ac-form__title'),
      el('p', { class: 'ac-muted' }, t('account.forgot.lead')),
      EmailField(shell, 'fg-email', 'username', email, setEmail, 'fg-error'),
      ErrorText('fg-error', error),
      Submit(shell, t('account.forgot.submit'), busy),
    );
  };
}

/** RQ2, RQ3: the reset link: a new password (and F removed if lost, N3), claimed on this device. */
function ResetCard(shell: Shell, token: string): Child {
  const { t, manager } = shell;
  const [password, setPassword] = createSignal('');
  const [lostFactor, setLostFactor] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [dueAt, setDueAt] = createSignal<number | null>(null);
  const strong = SheetNewPassword(shell, 'rs-pw', () => '', setPassword, t('account.password.new'), 'rs-error');
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    // Busy from the press on: the strength check waits for its scorer, and a second press meanwhile sends nothing.
    setBusy(true);
    const weak = await strong.check();
    if (weak !== null) {
      setBusy(false);
      setError(weak);
      focusField('rs-pw');
      return;
    }
    setError('');
    const outcome = await manager.claimReset(token, password(), lostFactor());
    setBusy(false);
    if (outcome.kind === 'ok') {
      setDueAt(outcome.value.dueAt);
      return;
    }
    setError(outcome.kind === 'refused' && outcome.error === 'invalid-link' ? t('account.error.resetLink') : actionProblem(shell, outcome));
  };
  return () => {
    const due = dueAt();
    if (due !== null) return SaidCard(shell, 'rs-title', t('account.reset.title'), t('account.reset.pending', { date: timeWords(shell, due) }), 'reset-pending');
    return el(
      'form',
      { class: 'ac-card ac-form reset-link', 'aria-labelledby': 'rs-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
      Heading('rs-title', t('account.reset.title'), 'ac-form__title'),
      el('p', { class: 'ac-muted' }, t('account.reset.lead')),
      strong.field,
      component(Checkbox, {
        id: 'rs-factor',
        label: t('account.reset.factor'),
        description: t('account.reset.factorHelp'),
        get checked() {
          return lostFactor();
        },
        onChange: (checked: boolean) => {
          setLostFactor(checked);
        },
      }),
      ErrorText('rs-error', error),
      Submit(shell, t('account.reset.submit'), busy),
      Later(shell),
    );
  };
}

/** The address of the account logged in on this device with a session to act with; null for none. */
function liveEmail(shell: Shell): string | null {
  const status = shell.account.status();
  return status.kind === 'account' && !status.signedOut ? status.email : null;
}

/** CH4: a change's link, confirmed on this device (the one that asked); another device's is said as such. */
function ConfirmCard(shell: Shell, token: string): Child {
  const { t, manager } = shell;
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [said, setSaid] = createSignal<string | null>(null);
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    setBusy(true);
    setError('');
    const outcome = await manager.confirm(token);
    setBusy(false);
    if (outcome.kind !== 'ok') {
      setError(actionProblem(shell, outcome));
      return;
    }
    // CG4: a change of E confirmed at the current address goes on with the new address's own link.
    setSaid(outcome.value.kind === 'check-email' ? t('account.outcome.checkNext') : advancedWords(shell, outcome.value, t('account.outcome.applied'), ''));
  };
  return () => {
    if (shell.account.status().kind === 'loading') return SaidCard(shell, 'cf-title', t('account.confirm.title'), t('account.page.loading'), 'confirm-loading');
    if (liveEmail(shell) === null && shell.homeScreen.state() !== 'none') return SaidCard(shell, 'cf-title', t('account.confirm.title'), t('account.link.homeScreen'), 'confirm-elsewhere');
    if (liveEmail(shell) === null) return SaidCard(shell, 'cf-title', t('account.confirm.title'), t('account.confirm.noAccount'), 'confirm-elsewhere');
    const words = said();
    if (words !== null) return SaidCard(shell, 'cf-title', t('account.confirm.title'), words, 'confirm-done');
    return el(
      'form',
      { class: 'ac-card ac-form confirm-link', 'aria-labelledby': 'cf-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
      Heading('cf-title', t('account.confirm.title'), 'ac-form__title'),
      el('p', { class: 'ac-muted' }, t('account.confirm.lead')),
      HomeScreenNote(shell),
      ErrorText('cf-error', error),
      Submit(shell, t('account.confirm.submit'), busy),
      Later(shell),
    );
  };
}

/** The enrolment card's step. */
type EnrolStep = { readonly kind: 'loading' } | { readonly kind: 'setup'; readonly setup: FactorSetup } | { readonly kind: 'codes'; readonly codes: readonly string[] } | { readonly kind: 'done' };

/** FX3, FX4: the enrolment's link: its setup (the app's code or the passkey), then the codes shown once, then done. */
function EnrolCard(shell: Shell, token: string): Child {
  const { t, manager } = shell;
  const [step, setStep] = createSignal<EnrolStep>({ kind: 'loading' });
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [saved, setSaved] = createSignal(false);
  // Deep-fix r3: a link opened in a fresh page lands before the account is read: the setup starts once the account is
  // logged in here (at once when it already is), and only once.
  let started = false;
  createEffect(() => {
    if (started || liveEmail(shell) === null) return;
    started = true;
    void manager.setUpEnrolment(token).then((outcome) => {
      if (outcome.kind === 'ok') setStep({ kind: 'setup', setup: outcome.value });
      else setError(actionProblem(shell, outcome));
    });
  });
  const confirm = (proof: NewFactorProof): void => {
    if (busy()) return;
    setBusy(true);
    setError('');
    void manager.confirmEnrolment(token, proof).then((outcome) => {
      setBusy(false);
      if (outcome.kind === 'ok') setStep({ kind: 'codes', codes: outcome.value });
      else setError(actionProblem(shell, outcome));
    });
  };
  /** The design's progress line over a step's heading ("Step 2 of 3": the choice was the sheet's first). */
  const progress = (step: number): Child => el('p', { class: 'ac-sheet__progress' }, t('account.sheet.step', { step, total: 3 }));
  const card = (title: string, ...content: Child[]): Child =>
    el('section', { class: 'ac-card ac-form enrol-link', 'aria-labelledby': 'en-title' }, progress(2), Heading('en-title', title, 'ac-form__title'), HomeScreenNote(shell), ...content, ErrorText('en-error', error));
  return () => {
    const email = liveEmail(shell);
    if (shell.account.status().kind === 'loading') return SaidCard(shell, 'en-title', t('account.enrol.title'), t('account.page.loading'), 'enrol-loading');
    if (email === null) return SaidCard(shell, 'en-title', t('account.enrol.title'), t(shell.homeScreen.state() === 'none' ? 'account.enrol.noAccount' : 'account.link.homeScreen'), 'enrol-elsewhere');
    const now = step();
    switch (now.kind) {
      case 'loading':
        return card(t('account.enrol.title'), el('p', { class: 'ac-muted' }, () => (error() === '' ? t('account.page.loading') : '')), () => (error() === '' ? null : CloseButton(shell)));
      case 'setup':
        return card(t(now.setup.kind === 'passkey' ? 'account.tf.title.passkey' : 'account.tf.title.app'), SetupStep(shell, now.setup, busy, confirm, setError));
      case 'codes':
        return el(
          'form',
          {
            class: 'ac-card ac-form enrol-link enrol-codes',
            'aria-labelledby': 'en-title',
            noValidate: true,
            onSubmit: (event: SubmitEvent) => {
              event.preventDefault();
              if (saved()) setStep({ kind: 'done' });
              else {
                setError(t('account.error.saved'));
                focusField('codes-saved');
              }
            },
          },
          progress(3),
          Heading('en-title', t('account.tf.title.codes'), 'ac-form__title'),
          CodesView(shell, now.codes, email, saved, setSaved),
          ErrorText('en-error', error),
          Submit(shell, t('account.sheet.continue'), busy, { off: () => !saved() }),
        );
      case 'done':
        return SaidCard(shell, 'en-title', t('account.tf.title.done'), t('account.tf.done'), 'enrol-link enrol-done', true);
    }
  };
}

/** DA3: the deletion's cancel link: nothing sent before the button is pressed; any device, no account needed. */
function KeepCard(shell: Shell, token: string): Child {
  const { t, manager } = shell;
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [kept, setKept] = createSignal(false);
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    setBusy(true);
    setError('');
    const outcome = await manager.cancelDeletionByLink(token);
    setBusy(false);
    if (outcome.kind === 'ok') setKept(true);
    else setError(outcome.kind === 'refused' && outcome.error === 'invalid-link' ? t('account.error.link') : actionProblem(shell, outcome));
  };
  return () =>
    kept()
      ? SaidCard(shell, 'kp-title', t('account.keepLink.title'), t('account.keepLink.done'), 'keep-done')
      : el(
          'form',
          { class: 'ac-card ac-form keep-link', 'aria-labelledby': 'kp-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
          Heading('kp-title', t('account.keepLink.title'), 'ac-form__title'),
          el('p', { class: 'ac-muted' }, t('account.keepLink.lead')),
          ErrorText('kp-error', error),
          Submit(shell, t('account.keepLink.submit'), busy),
          Later(shell),
        );
}

/** LK: the card of a mailed link of `kind`, over any screen; a link that holds no token says so, nothing sent. */
export function LinkCard(shell: Shell, kind: LinkStep): Child {
  const { t } = shell;
  const token = shell.ui.linkToken();
  if (token === null || token === '') return SaidCard(shell, 'lk-title', t('account.link.badTitle'), t('account.link.bad'), 'link-bad');
  switch (kind) {
    case 'reset':
      return ResetCard(shell, token);
    case 'confirm':
      return ConfirmCard(shell, token);
    case 'enrol':
      return EnrolCard(shell, token);
    case 'keep':
      return KeepCard(shell, token);
  }
}
